import type {
  ClientOrganization,
  ClientOrganizationRoleEnum,
  ClientOrganizationTagsEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { ClientGlobalRoleAssignmentRoleEnum } from '@DigitaleDoerfer/digitale-doerfer-api';

import { isOrganizationType } from '@/shared/utils/organization';

export enum OrganizationAccessState {
  Error = 'error',
  Granted = 'granted',
  Denied = 'denied'
}

export type OrganizationAccessRequirement = {
  /**
   * Roles that grant access when held by the caller in the target organization
   * (any-of). When omitted, the guard is **secure by default**: access falls
   * back to `ADMIN`-only (plus any global organization admin). To open a
   * section to everyone — including non-members — set `ignoreRoles` instead of
   * leaving `roles` empty.
   */
  roles?: ClientOrganizationRoleEnum[];
  /**
   * Tags the target organization must carry (all-of). A hard feature gate on
   * the organization itself (e.g. TAO-only sections). Applied to every
   * caller — including global admins and when `ignoreRoles` is set.
   */
  requiredTags?: ClientOrganizationTagsEnum[];
  /**
   * Tags the target organization must carry at least one of (any-of). A soft
   * feature gate used when multiple organization types unlock the same section
   * (e.g. `TERRITORIAL_AUTHORITY` or `ADMIN`). Applied to every caller —
   * including global admins and when `ignoreRoles` is set. Combined with
   * `requiredTags` via logical AND (both gates must pass).
   */
  requiredTagsAny?: ClientOrganizationTagsEnum[];
  /**
   * When `true`, the role / membership check is waived: any authenticated user
   * is granted, regardless of role or organization membership. The
   * `requiredTags` gate still applies. Use this for the deliberate "no org role
   * required" case; omit it to keep the secure `ADMIN`-only default.
   */
  ignoreRoles?: boolean;
};

/**
 * The precomputed permission context value for a single organization. Produced
 * once by `OrganizationAccessProvider` and evaluated by
 * {@link resolveOrganizationAccess}.
 */
export type OrganizationAccessContextValue = {
  isError: boolean;
  organization: ClientOrganization | undefined;
  /**
   * The global (entity-independent) roles the caller holds. These roles live in
   * the app-state context and take priority over the per-organization role: any
   * global role grants full organization-admin access to every organization.
   */
  globalRoles: ClientGlobalRoleAssignmentRoleEnum[];
  /** The caller's direct role (`ADMIN | FUNKER`) in the organization, if any. */
  ownRole: ClientOrganizationRoleEnum | undefined;
};

const DEFAULT_ROLES: ClientOrganizationRoleEnum[] = ['ADMIN'];

/**
 * Whether the caller holds a global role that grants access to every
 * organization. Any of these platform-level global roles bypasses the
 * per-organization role check (mirrors the backend, which grants all three full
 * organization access).
 */
const hasGlobalOrganizationAccess = (
  globalRoles: ClientGlobalRoleAssignmentRoleEnum[]
): boolean =>
  globalRoles.some(
    (role) =>
      role === ClientGlobalRoleAssignmentRoleEnum.SuperAdmin ||
      role === ClientGlobalRoleAssignmentRoleEnum.GlobalConfigurationAdmin ||
      role === ClientGlobalRoleAssignmentRoleEnum.OrganizationConfigurationAdmin
  );

/**
 * Pure evaluation of the organization access rules.
 *
 * Order:
 * 1. error passes through
 * 2. without a loaded organization we cannot evaluate tags / roles → denied
 * 3. `requiredTags` is a hard organization feature gate — applies to everyone,
 *    including global admins (tags and roles work in unison, never exclude
 *    each other). `requiredTagsAny` is the any-of counterpart and is evaluated
 *    as a logical AND alongside `requiredTags`.
 * 4. a global organization admin bypasses the per-organization role check
 * 5. `ignoreRoles` → granted to any authenticated user
 * 6. secure default: no `roles` given falls back to `ADMIN`-only
 * 7. caller holds one of the required `roles` in the organization → granted
 * 8. otherwise denied
 */
const resolveOrganizationAccess = (
  contextValue: OrganizationAccessContextValue,
  {
    roles,
    requiredTags,
    requiredTagsAny,
    ignoreRoles = false
  }: OrganizationAccessRequirement = {}
): OrganizationAccessState => {
  const { isError, organization, globalRoles, ownRole } = contextValue;

  if (isError) {
    return OrganizationAccessState.Error;
  }

  // Without a loaded organization we cannot evaluate tags / roles.
  if (organization === undefined) {
    return OrganizationAccessState.Denied;
  }

  // Tags feature gate: if the organization does not have all required tags,
  // access is denied — regardless of global roles, `ignoreRoles`, or
  // per-organization roles. Tags are a property of the organization, not of
  // the caller, so no caller privilege can waive them.
  if (requiredTags && requiredTags.length > 0) {
    const allTagsPresent = requiredTags.every((tag) =>
      isOrganizationType(tag, organization)
    );
    if (!allTagsPresent) {
      return OrganizationAccessState.Denied;
    }
  }

  // Any-of tags feature gate: the organization must carry at least one of the
  // listed tags. Same hard-gate semantics as `requiredTags` (applies to global
  // admins and `ignoreRoles` alike). Combined via logical AND.
  if (requiredTagsAny && requiredTagsAny.length > 0) {
    const anyTagPresent = requiredTagsAny.some((tag) =>
      isOrganizationType(tag, organization)
    );
    if (!anyTagPresent) {
      return OrganizationAccessState.Denied;
    }
  }

  // A caller with global organization access bypasses the per-organization
  // role check (but never the tag gate above).
  if (hasGlobalOrganizationAccess(globalRoles)) {
    return OrganizationAccessState.Granted;
  }

  if (ignoreRoles) {
    return OrganizationAccessState.Granted;
  }

  const requiredRoles = roles && roles.length > 0 ? roles : DEFAULT_ROLES;
  if (ownRole !== undefined && requiredRoles.includes(ownRole)) {
    return OrganizationAccessState.Granted;
  }

  return OrganizationAccessState.Denied;
};

export default resolveOrganizationAccess;
